import { observable, makeObservable, action, runInAction, computed } from 'mobx'
import {
  type FirestoreRoomStateCollaborationData,
  upsertRoomStateCollaborationDataInTransaction,
  upsertRoomStateCollaborationData,
} from '../../../firestore/RoomStateCollaborationData'
import type { RoomStateCollaborationData } from '../../../models/RoomStateCollaborationData'
import { SimpleObservableValue } from '../../../stores/SimpleObservableValue'
import { EventEmitterTyped } from '../../../util/EventEmitterTyped'
import { Cubit } from '../../core'
import { CollaborationDataType } from '../../MeetingCubit'
import type { MeetingQuestion } from '../MeetingQuestion'
import { SchemaParseError, JSONParseError } from './errors'
import type { InteractiveFormController } from './InteractiveFormController'
import {
  type JSONSchemaOutputValidatorError,
  JSONSchemaOutputValidator,
} from './JSONSchemaValidator'
import {
  aiResponseSchema,
  formSchema,
  type InteractiveFormSchema,
  formValidationSchema,
  type InteractiveFormValidationSchema,
  type InteractiveFormProperty,
  type InteractiveFormPropertyGroup,
} from './schema'
import { Timestamp } from 'firebase/firestore'

export type Options = unknown

export type SuggestionValue =
  | string
  | number
  | boolean
  | string[]
  | number[]
  | boolean[]

export class InteractiveFormElementSubController extends Cubit {
  controller: InteractiveFormController
  element: MeetingQuestion
  options: Options

  wantsButtonsAtBottom = new SimpleObservableValue(true)
  wantsHeaderAndPadding = new SimpleObservableValue(true)
  showTimerForRequest = new SimpleObservableValue<string | null>(null)

  initialized = new SimpleObservableValue(false)

  // values - field name to value
  _values = observable.map<string, unknown>({})

  // suggestions - field name to suggestion
  suggestions = observable.map<string, SuggestionValue>({}, { deep: false })

  @observable
  valid = true

  @observable
  errors: Record<string, JSONSchemaOutputValidatorError[]> | null = null

  aiResponses = observable.array<RoomStateCollaborationData>([])
  @observable
  aiResponsesLoaded = false

  aiResponsesSeen = new Set<string>()

  aiRequests = observable.array<RoomStateCollaborationData>([])

  events = new EventEmitterTyped<{
    aiResponse: [
      {
        requestId: string
        success: boolean
        response?: string
        error?: string
      },
    ]
    suggestionApplied: [field: string, value: unknown]
  }>()

  timeouts: Record<string, NodeJS.Timeout> = {}

  constructor(
    controller: InteractiveFormController,
    element: MeetingQuestion,
    options: Options = {}
  ) {
    super()
    this.controller = controller
    this.element = element
    this.options = options

    makeObservable(this)
  }

  initialize(): void {
    this.loadValuesFromAnswer()
    this.startListeningToCollaborationData()
    this.sendFormElementStartedRequest()
    this.initialized.setValue(true)
  }

  dispose(): symbol {
    this.events.removeAllListeners()
    for (const key in this.timeouts) {
      clearTimeout(this.timeouts[key])
      delete this.timeouts[key]
    }
    return super.dispose()
  }

  runValidation() {
    const parsed = this.validationSchema
    if (!parsed.success) {
      return { valid: false, errors: parsed.error }
    }

    const validator = new JSONSchemaOutputValidator(parsed.data, this.values)
    const result = validator.validate()

    return result.valid
  }

  @action
  validate(fieldName?: string) {
    const parsed = this.validationSchema
    if (!parsed.success) {
      return { valid: false, errors: parsed.error }
    }

    const validator = new JSONSchemaOutputValidator(
      parsed.data,
      this.values,
      fieldName
    )
    const result = validator.validate()

    if (fieldName) {
      if (result.errors[fieldName]) {
        const errors = this.errors || {}
        const newErrors = { ...errors, [fieldName]: result.errors[fieldName] }
        if (Object.keys(newErrors).length === 0) {
          this.valid = true
          this.errors = null
        } else {
          this.valid = false
          this.errors = newErrors
        }
      } else {
        if (this.errors) {
          const newErrors = { ...this.errors }
          delete newErrors[fieldName]
          if (Object.keys(newErrors).length === 0) {
            this.valid = true
            this.errors = null
          } else {
            this.valid = false
            this.errors = newErrors
          }
        } else {
          this.valid = true
          this.errors = null
        }
      }
    } else {
      this.valid = result.valid
      this.errors = result.valid ? null : result.errors
    }

    return result
  }

  @action
  loadValuesFromAnswer() {
    const answerString = this.element.groupAnswer?.data.answerString
    if (!answerString) return

    try {
      const parsed = JSON.parse(answerString)
      if (typeof parsed === 'object' && parsed !== null) {
        this.values.replace(parsed)
      }
    } catch {
      // ignore
    }
  }

  startListeningToCollaborationData() {
    this.addReaction({
      whenThisChanges: () => this.controller.meeting.collaborationData.models,
      thenRunThisCode: (allDatas) => {
        // only focus on suggestions for this question
        const datas = allDatas.filter(
          (d) => d.data.questionId === this.element.id
        )

        this.processFormElementPartials(datas)

        this.processAIResponses(allDatas)

        this.processSuggestions(datas)

        this.processAIRequests(datas)
      },
      fireImmediately: true,
    })
  }

  @action
  processAIRequests(datas: RoomStateCollaborationData[]) {
    const aiRequests = datas.filter(
      (d) =>
        d.data.collaborationDataType === CollaborationDataType.aiAgentRequest
    )

    this.aiRequests.replace(aiRequests)
  }

  // process AI responses - look for new ones and emit events
  // to power the async request/response flow
  @action
  processAIResponses(datas: RoomStateCollaborationData[]) {
    const aiResponses = datas.filter(
      (d) =>
        d.data.collaborationDataType === CollaborationDataType.aiAgentResponse
    )

    runInAction(() => {
      if (
        // loaded means we did the first load - we skip first load
        this.aiResponsesLoaded
      ) {
        for (const resp of aiResponses) {
          // skip ones we have already seen
          if (this.aiResponsesSeen.has(resp.id)) continue
          const data = resp.data.data ?? {}
          const parsed = aiResponseSchema.safeParse(data)
          if (parsed.success) {
            this.events.emit('aiResponse', {
              success: true,
              response: parsed.data.response,
              requestId: parsed.data.requestId,
            })
          }
        }
      }

      for (const resp of aiResponses) {
        if (!this.aiResponsesSeen.has(resp.id)) {
          this.aiResponsesSeen.add(resp.id)
        }
      }

      this.aiResponses.replace(aiResponses)
      this.aiResponsesLoaded = true
    })
  }

  @action
  processFormElementPartials(datas: RoomStateCollaborationData[]) {
    const parsedSchema = this.schema.data
    const neverSuggestFields =
      parsedSchema?._generationRules?.neverSuggestFields ?? []
    const formElementPartialDatas = datas.filter(
      (d) =>
        d.data.collaborationDataType ===
        CollaborationDataType.formElementPartial
    )

    const nonReviewedDatas = formElementPartialDatas.filter(
      (d) => d.data.reviewed !== true
    )

    const newSuggestions: {
      [key: string]: string | number | boolean | string[] | number[] | boolean[]
    } = {}

    // Unpack the collab data into individual suggestions
    for (const d of nonReviewedDatas) {
      const data = d.data.data ?? {}

      const keys = Object.keys(data)

      for (const key of keys) {
        const value = data[key]

        if (value === undefined || value === null) continue

        // skip if in neverSuggestFields
        if (neverSuggestFields.includes(key)) continue

        newSuggestions[key] = value
      }
    }

    const items: {
      id: string
      data: Partial<FirestoreRoomStateCollaborationData>
      merge?: boolean
    }[] = []

    if (Object.keys(newSuggestions).length > 0) {
      for (const key in newSuggestions) {
        // already has a value, ignore suggestion
        // if (this.values.get(key)) continue
        const value = this.values.get(key)

        if (JSON.stringify(value) === JSON.stringify(newSuggestions[key])) {
          continue
        }

        if (neverSuggestFields.includes(key)) continue

        this.suggestions.set(key, newSuggestions[key])
      }

      const suggestionsId = `suggestions-${this.element.id}`
      const suggestionsRecord: Record<string, unknown> = Object.fromEntries(
        this.suggestions.entries()
      )

      items.push({
        id: suggestionsId,
        data: {
          data: suggestionsRecord,
          questionId: this.element.id,
          collaborationDataType:
            CollaborationDataType.interactiveFormSuggestions,
        },
        merge: true,
      })
    }

    for (const d of nonReviewedDatas) {
      if (d.data.reviewed !== true) {
        items.push({
          id: d.id,
          data: {
            questionId: d.data.questionId,
            reviewed: true,
          },
          merge: true,
        })
      }
    }

    // setTimeout to jump out of processing loop
    setTimeout(() => {
      upsertRoomStateCollaborationDataInTransaction(
        this.controller.meeting.repository,
        {
          roomId: this.controller.meeting.roomId,
          items,
        }
      )
    }, 500)
  }

  @action
  processSuggestions(datas: RoomStateCollaborationData[]) {
    const suggestionsId = `suggestions-${this.element.id}`
    const suggestionsData = datas.find((d) => d.id === suggestionsId)
    if (!suggestionsData) return

    const data = suggestionsData.dataToJs.data ?? {}

    this.suggestions.replace(data as Map<string, SuggestionValue>)

    if (this.controller.yoloMode.value) {
      // in yolo mode, apply all suggestions automatically
      for (const [key, value] of this.suggestions.entries()) {
        this.applySuggestion(key, value)
      }
    }
  }

  removeSuggestion(key: string) {
    const suggestionsId = `suggestions-${this.element.id}`
    const suggestionsRecord: Record<string, unknown> = Object.fromEntries(
      this.suggestions.entries()
    )
    suggestionsRecord[key] = undefined

    this.element.upsertCollaborationRawData(suggestionsId, {
      data: suggestionsRecord,
      type: CollaborationDataType.interactiveFormSuggestions,
    })
  }

  suggestionAcceptedTimeput: NodeJS.Timeout | null = null

  @computed
  get isValid() {
    return this.runValidation()
  }

  @computed
  get values() {
    if (this.controller.isUserListenedToByBot) {
      return this._values
    } else {
      // followers get values from the answer
      const answerString = this.element.groupAnswer?.data.answerString
      if (!answerString) {
        this._values.clear()
        return this._values
      }

      const answer = JSON.parse(answerString)
      this._values.replace(answer)
      return this._values
    }
  }

  @computed
  get suggestionsDocument() {
    const suggestionsId = `suggestions-${this.element.id}`
    return this.controller.meeting.collaborationData.models.find(
      (model) => model.id === suggestionsId
    ) as RoomStateCollaborationData | null
  }

  getStringListValue(field: string): string[] {
    const val = this.values.get(field)
    if (Array.isArray(val)) {
      return val.filter((v) => typeof v === 'string') as string[]
    }
    return []
  }

  getStringValue(field: string): string | undefined {
    const val = this.values.get(field)
    if (typeof val === 'string') {
      return val
    }
    return undefined
  }

  getObjectValue<T = Record<string, unknown>>(field: string): T | undefined {
    const val = this.values.get(field)
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      return val as T
    }
    return undefined
  }

  getRawValue(field: string): unknown {
    return this.values.get(field)
  }

  @action
  setStringValue(
    field: string,
    value: string | undefined,
    options: { silent?: boolean } = {}
  ) {
    const currentValue = this.values.get(field)
    if (currentValue === value) return
    if (value === undefined) {
      this.values.delete(field)
    } else {
      this.values.set(field, value)
    }
    if (!options.silent) this.onChange(field)
  }

  @action
  setStringListValue(field: string, value: string[] | undefined) {
    const currentValue = this.values.get(field)
    if (JSON.stringify(currentValue) === JSON.stringify(value)) return
    if (value === undefined) {
      this.values.delete(field)
    } else {
      this.values.set(field, value)
    }
    this.onChange(field)
  }

  @action
  setRawValue(
    field: string,
    value: unknown,
    options: { silent?: boolean } = {}
  ) {
    const currentValue = this.values.get(field)
    if (JSON.stringify(currentValue) === JSON.stringify(value)) return
    if (value === undefined) {
      this.values.delete(field)
    } else {
      this.values.set(field, value)
    }
    if (!options.silent) this.onChange(field)
  }

  onChange(fieldName?: string) {
    // if we have errors, re-validate
    this.validate(fieldName)

    this.controller.sendFormUpdate(this.values)

    this.storeAnswer()
  }

  storeAnswer() {
    this.element.upsertAnswer({
      isGroupAnswer: true,
      submitted: false,
      answer: 0,
      answerString: JSON.stringify(Object.fromEntries(this.values)),
    })
  }

  @computed
  get schema() {
    try {
      const schema = this.element.data.schema || '{}'
      const result = formSchema.safeParse(JSON.parse(schema.trim()))
      if (!result.success) {
        return {
          success: false as const,
          error: new SchemaParseError(result.error),
        }
      }
      return {
        success: true as const,
        data: result.data as InteractiveFormSchema,
      }
    } catch {
      return { success: false as const, error: new JSONParseError() }
    }
  }

  @computed
  get validationSchema() {
    try {
      const schema = this.element.data.schema || '{}'
      const result = formValidationSchema.safeParse(JSON.parse(schema.trim()))
      if (!result.success) {
        return {
          success: false as const,
          error: new SchemaParseError(result.error),
        }
      }
      return {
        success: true as const,
        data: result.data as InteractiveFormValidationSchema,
      }
    } catch {
      return { success: false as const, error: new JSONParseError() }
    }
  }

  @computed
  get groupsAndProperties() {
    const parsed = this.schema
    if (!parsed.success) {
      return { groups: [], properties: {} }
    }
    const schema = parsed.data

    const freeProperties: Record<string, InteractiveFormProperty> = {
      ...schema.properties,
    }
    const layoutGroups = schema._layout?.groups || []
    const propertyGroups: InteractiveFormPropertyGroup[] = []
    for (const group of layoutGroups) {
      const propertyGroup: InteractiveFormPropertyGroup = {
        id: group.id,
        component: group.component,
        title: group.title,
        properties: {},
      }
      propertyGroups.push(propertyGroup)

      for (const field of group.fields) {
        propertyGroup.properties[field] = schema.properties[field]
        // remove from freeProperties
        delete freeProperties[field]
      }
    }

    return {
      groups: propertyGroups,
      properties: freeProperties,
    }
  }

  markSuggestionReviewed(field: string) {
    if (field) {
      this.removeSuggestion(field)
      this.suggestions.delete(field)
    }
  }

  @action
  applySuggestion(field: string, value: unknown) {
    this.values.set(field, value)
    this.markSuggestionReviewed(field)
    this.storeAnswer()
    this.controller.suggestionAccepted(this.values)
    this.validate(field)
    this.events.emit('suggestionApplied', field, value)
  }

  @action
  rejectSuggestion(field: string) {
    this.markSuggestionReviewed(field)
    this.controller.suggestionRejected(this.values)
  }

  sendFormElementStartedRequest = () => {
    if (!this.element) return
    const parsed = this.schema
    if (!parsed.success) return
    const parsedSchema = parsed.data
    // - send which element I am on (we are on form element [questionId])
    // - also tell which fields I want autocomplete form (_fieldMeta has requestAutocomplete: true and the field is empty)
    // - requestResponse: true
    const fieldsNeedingAutocomplete =
      parsedSchema._generationRules?.autoSuggestFields || []
    const fieldsToNeverAutocomplete =
      parsedSchema._generationRules?.neverSuggestFields || []

    this.controller.sendFormElementStartedRequest(
      this.element.id,
      fieldsNeedingAutocomplete,
      fieldsToNeverAutocomplete
    )
  }

  startRequest(key: string, callback: () => string): string {
    const activeRequestId = this.getActiveRequestId(key)

    if (activeRequestId) {
      console.log(`Request ${key} is already active as ${activeRequestId}`) // eslint-disable-line no-console
      return activeRequestId
    }

    console.log(`Starting request ${key}`) // eslint-disable-line no-console

    const requestId = callback()

    this.element.upsertCollaborationRawData(`request-${requestId}`, {
      data: {
        requestId,
        requestKey: key,
        startedAt: new Date(),
        userId: this.controller.meeting.currentUser.uid,
        state: 'running',
      },
      type: CollaborationDataType.aiAgentRequest,
    })

    return requestId
  }

  getActiveRequest(key: string): RoomStateCollaborationData | undefined {
    const found = this.aiRequests
      // find matching the key
      .filter((r) => {
        const data = r.data.data ?? {}
        return data.requestKey === key
      })
      // only ones that are running or complete
      .filter((r) => {
        const data = r.data.data ?? {}
        const state = data.state
        return state === 'running' || state === 'complete'
      })
      .find((d) => {
        const data = d.data.data ?? {}
        const requestId = data?.requestId
        if (requestId) {
          // only return if:
          //  - it has a requestId
          //  - is "running"
          //  - or is "complete" and has an response that has not been marked as reviewed
          const state = data?.state
          if (state === 'running') {
            // if the request is running but less than 10 minutes old, consider it active
            const startedAt = data?.startedAt
            if (startedAt && startedAt instanceof Timestamp) {
              const age = Date.now() - startedAt.toDate().getTime()
              if (age < 10 * 60 * 1000) {
                return true
              }
              // too old, ignore and mark for cleanup
              setTimeout(() => this.cleanupOldRequest(d.id), 500)
              return false
            }

            return true
          }
          if (state === 'complete') {
            const response = this.aiResponses.find((r) => {
              const rData = r.data.data ?? {}
              return rData.requestId === requestId
            })
            if (response && response.data.reviewed !== true) return true
          }
        }
        return false
      })

    console.log(`Active request for ${key} is`, found?.id) // eslint-disable-line no-console

    return found
  }

  cleanupOldRequest(id: string) {
    this.updateRequestState(id, 'timeout')
  }

  hasActiveRequest(key: string): boolean {
    return !!this.getActiveRequest(key)
  }

  getActiveRequestId(key: string): string | null {
    const found = this.getActiveRequest(key)
    if (found && found.data.data?.requestId) {
      return found.data.data.requestId as string
    }
    return null
  }

  markResponseAsReviewed(requestId: string) {
    const response = this.getResponse(requestId)
    if (response && response.data.reviewed !== true) {
      this.element.upsertCollaborationRawData(
        response.id,
        {
          reviewed: true,
          type: CollaborationDataType.aiAgentResponse,
        },
        { merge: true }
      )
    }
  }

  async waitForResponse(
    requestId: string
  ): Promise<
    { success: true; response: string } | { success: false; error: string }
  > {
    return new Promise((resolve) => {
      console.log(`Waiting for AI response ${requestId}`) // eslint-disable-line no-console
      const listener = (data: {
        success: boolean
        requestId: string
        response?: string
        error?: string
      }) => {
        // if not for me, ignore
        if (data.requestId !== requestId) return

        // we no longer need to listen
        this.events.removeListener('aiResponse', listener)
        if (this.timeouts[requestId]) {
          clearTimeout(this.timeouts[requestId])
          delete this.timeouts[requestId]
        }

        if (data.success) {
          this.updateRequestState(requestId, 'complete')
          resolve({ success: true, response: data.response || '' })
        } else {
          this.updateRequestState(requestId, 'error')
          resolve({ success: false, error: data.error || 'Unknown error' })
        }
      }

      this.events.on('aiResponse', listener)

      this.timeouts[requestId] = setTimeout(() => {
        this.events.removeListener('aiResponse', listener)
        this.updateRequestState(requestId, 'timeout')
        resolve({
          success: false,
          error: `Timeout waiting for AI response ${requestId}`,
        })
      }, 1200000)
    })
  }

  updateRequestState(
    requestId: string,
    state: 'running' | 'complete' | 'error' | 'timeout'
  ) {
    this.element.upsertCollaborationRawData(
      `request-${requestId}`,
      {
        data: { state: state },
        type: CollaborationDataType.aiAgentRequest,
      },
      {
        merge: true,
      }
    )
  }

  getResponse(requestId: string) {
    return this.aiResponses.find((d) => {
      const data = d.data.data ?? {}
      return data.requestId === requestId
    })
  }

  @action
  upsertCollaborationData = async ({
    type,
    id,
    data,
  }: {
    type: CollaborationDataType
    id: string
    data: Record<string, unknown>
  }) => {
    return upsertRoomStateCollaborationData(
      this.controller.meeting.repository,
      {
        roomId: this.controller.meeting.roomId,
        id: id,
        data: {
          updatedAt: new Date(),
          collaborationDataType: type,
          userId: this.controller.meeting.currentUser.uid,
          questionId: this.element.id,
          data: data,
        },
      }
    )
  }
}
